import { useState, useMemo, useEffect } from 'react';
import { CheckCircle, AlertCircle } from 'lucide-react';
import { BrgEvent, BrgGroup, CalendarDay, NewEventFormData } from './types';
import EventDetailModal from './components/EventDetailModal';
import DayViewModal from './components/DayViewModal';
import NewEventModal from './components/NewEventModal';
import CalendarGrid from './components/CalendarGrid';
import { generateCalendarDays, sortEventsByTimeAndTitle } from './utils';
import { eventsApi } from '../../api/events';
import { brgsApi } from '../../api/brgs';
import { useAuth } from '../../auth/AuthContext';
import { FilterSelect } from '../../components/shared/FilterSelect';
import { brgColorOrDefault } from '../../lib/colorUtils';

/**
 * Calendar Page Component
 * Displays a monthly calendar with BRG events, filtering, search, and event management
 */
interface Toast {
  id: string;
  type: 'success' | 'error';
  message: string;
}

export default function CalendarPage() {
  // Auth context
  const auth = useAuth();
  const currentUserEmail = auth?.email || 'unknown@local.dev';
  const currentUserName = auth?.name || 'User';
  const isAdmin = auth?.roles?.includes('BRG.Admin') || false;

  // State management
  const [currentDate, setCurrentDate] = useState(() => {
    const today = new Date();
    return new Date(today.getFullYear(), today.getMonth(), 1);
  });
  const [selectedBrgFilter, setSelectedBrgFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [events, setEvents] = useState<BrgEvent[]>([]);
  const [selectedEvent, setSelectedEvent] = useState<BrgEvent | null>(null);
  const [selectedDay, setSelectedDay] = useState<CalendarDay | null>(null);
  const [showNewEventModal, setShowNewEventModal] = useState(false);
  const [editingEvent, setEditingEvent] = useState<BrgEvent | null>(null);
  const [deleteConfirmation, setDeleteConfirmation] = useState<{ eventId: number; eventName: string } | null>(null);
  const [formBrgGroups, setFormBrgGroups] = useState<BrgGroup[]>([]); // Filtered BRGs for form access
  const [legendBrgGroups, setLegendBrgGroups] = useState<BrgGroup[]>([]); // Full BRG color key from database
  const [toasts, setToasts] = useState<Toast[]>([]);

  // Helper function to add toast notification
  const addToast = (type: 'success' | 'error', message: string) => {
    const id = Math.random().toString(36).substr(2, 9);
    const newToast: Toast = { id, type, message };
    setToasts(prev => [...prev, newToast]);

    // Auto-remove toast after 4 seconds
    setTimeout(() => {
      setToasts(prev => prev.filter(t => t.id !== id));
    }, 4000);
  };

  const showSuccessMessage = (message: string) => {
    addToast('success', message);
  };

  const showErrorMessage = (message: string) => {
    addToast('error', message);
  };

  // Fetch BRGs from database on component mount
  useEffect(() => {
    if (!auth.isAuthenticated) return;
    const fetchBrgs = async () => {
      try {
        const brgsData = await brgsApi.list();
        
        // Convert API data to BrgGroup format from DB values only.
        const apiBrgs: BrgGroup[] = brgsData.map(apiData => {
          return {
            id: typeof apiData.brgId === 'string' ? parseInt(apiData.brgId, 10) : apiData.brgId,
            name: apiData.name,
            color: brgColorOrDefault(apiData.colorCode),
          };
        });
        
        // For the form dropdown and color legend, sort alphabetically by name
        // Admins see all BRGs, Leads see only their assigned BRGs
        if (isAdmin) {
          const sortedBrgs = [...apiBrgs].sort((a, b) => a.name.localeCompare(b.name));
          setFormBrgGroups(sortedBrgs);
          setSelectedBrgFilter('all');
        } else {
          // Leads already get only their assigned BRGs from the API
          const sortedBrgs = [...apiBrgs].sort((a, b) => a.name.localeCompare(b.name));
          setFormBrgGroups(sortedBrgs);
          // For single BRG, default to that BRG; for multiple, default to 'all'
          if (sortedBrgs.length === 1) {
            setSelectedBrgFilter(sortedBrgs[0].id.toString());
          } else {
            setSelectedBrgFilter('all');
          }
        }
      } catch (error) {
        console.error('Failed to fetch BRGs:', error);
        setFormBrgGroups([]);
        setSelectedBrgFilter('all');
      }
    };

    fetchBrgs();
  }, [isAdmin, auth.isAuthenticated]);

  // Fetch full BRG color key from database (for Admin and Lead legends)
  useEffect(() => {
    if (!auth.isAuthenticated) return;
    const fetchColorKey = async () => {
      try {
        const colorKeyData = await brgsApi.listColorKey();
        const mappedColorKeys: BrgGroup[] = colorKeyData.map(item => ({
          id: item.brgId,
          name: item.name,
          color: brgColorOrDefault(item.colorCode),
        }));

        setLegendBrgGroups(mappedColorKeys.sort((a, b) => a.name.localeCompare(b.name)));
      } catch (error) {
        console.error('Failed to fetch BRG color key:', error);
        setLegendBrgGroups([]);
      }
    };

    fetchColorKey();
  }, [isAdmin, auth.isAuthenticated]);

  // Fetch events from database on component mount and when auth state changes
  useEffect(() => {
    const fetchEvents = async () => {
      try {
        const eventsData = await eventsApi.list();
        
        // Map API response to BrgEvent format
        const mappedEvents: BrgEvent[] = eventsData.map(event => {
          // Find BRG color from legendBrgGroups (all BRGs, not just assigned)
          // This ensures event colors work even for events from non-assigned BRGs (for Leads)
          const brg = legendBrgGroups.find(b => b.id === event.brgId);
          
          // Convert acceptedAttendees from number to string
          const attendeesStr = event.acceptedAttendees > 0 ? `${event.acceptedAttendees} members` : '';
          
          return {
            id: event.eventId,
            eventName: event.eventName,
            brgId: event.brgId,
            brgName: event.brgName,
            brgColor: brgColorOrDefault(brg?.color),
            date: event.eventDate,
            time: event.eventTime,
            organizer: event.eventOrganizer,
            location: event.eventLocation || 'Microsoft Teams',
            acceptedAttendees: attendeesStr,
            eventDescription: event.eventDescription || '',
            createdBy: event.createdByEmail || '',
          };
        });
        
        setEvents(mappedEvents);
      } catch (error) {
        console.error('Failed to fetch events:', error);
        // Fall back to empty array if API fails
        setEvents([]);
      }
    };

    // Always fetch events when auth state or user changes
    // legendBrgGroups being available ensures we have color mapping ready
    if (currentUserEmail !== 'unknown@local.dev') {
      fetchEvents();
    }
  }, [isAdmin, currentUserEmail, legendBrgGroups]);

  // Calculate calendar days for current month
  const calendarDays = useMemo(() => {
    return generateCalendarDays(currentDate, events);
  }, [currentDate, events]);

  // Filter and search events for current month
  const filteredAndSearchedEvents = useMemo(() => {
    return calendarDays.map(day => ({
      ...day,
      events: day.events
        .filter(event => {
          // Filter by BRG
          if (selectedBrgFilter !== 'all' && event.brgId !== parseInt(selectedBrgFilter)) {
            return false;
          }
          // Search by event name or BRG name (case-insensitive)
          if (searchQuery) {
            const query = searchQuery.toLowerCase();
            return (
              event.eventName.toLowerCase().includes(query) ||
              event.brgName.toLowerCase().includes(query)
            );
          }
          return true;
        })
        .sort(sortEventsByTimeAndTitle),
    }));
  }, [calendarDays, selectedBrgFilter, searchQuery]);

  // Navigate months
  const handlePrevMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1));
  };

  const handleNextMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1));
  };

  // Event handlers
  const handleEventClick = (event: BrgEvent) => {
    setSelectedEvent(event);
  };

  const handleDayClick = (day: CalendarDay) => {
    if (day.events.length > 0) {
      setSelectedDay(day);
    }
  };

  const handleSaveEvent = async (formData: NewEventFormData) => {
    try {
      if (editingEvent) {
        // Update existing event
        await eventsApi.update(editingEvent.id, {
          eventName: formData.eventName,
          brgId: formData.brgId,
          eventDate: formData.date,
          eventTime: formData.time,
          eventOrganizer: formData.organizer,
          eventLocation: formData.location,
          acceptedAttendees: formData.acceptedAttendees,
          eventDescription: formData.eventDescription,
        });
        showSuccessMessage('Event has been saved to the BRG calendar');
      } else {
        // Create new event
        await eventsApi.create({
          eventName: formData.eventName,
          brgId: formData.brgId,
          eventDate: formData.date,
          eventTime: formData.time,
          eventOrganizer: formData.organizer,
          eventLocation: formData.location,
          acceptedAttendees: formData.acceptedAttendees,
          eventDescription: formData.eventDescription,
        });
        showSuccessMessage('Event has been saved to the BRG calendar');
      }

      // Refresh events from database to ensure consistency
      const eventsData = await eventsApi.list();
      const mappedEvents: BrgEvent[] = eventsData.map(event => {
        // Use legendBrgGroups (all BRGs) to find colors, not brgGroups (only assigned BRGs)
        // This ensures events from unassigned BRGs still display correctly
        const brg = legendBrgGroups.find(b => b.id === event.brgId);
        const attendeesStr = event.acceptedAttendees > 0 ? `${event.acceptedAttendees} members` : '';
        
        return {
          id: event.eventId,
          eventName: event.eventName,
          brgId: event.brgId,
          brgName: event.brgName,
          brgColor: brgColorOrDefault(brg?.color),
          date: event.eventDate,
          time: event.eventTime,
          organizer: event.eventOrganizer,
          location: event.eventLocation || 'Microsoft Teams',
          acceptedAttendees: attendeesStr,
          eventDescription: event.eventDescription || '',
          createdBy: event.createdByEmail || '',
        };
      });

      setEvents(mappedEvents);
      setShowNewEventModal(false);
      setEditingEvent(null);
    } catch (error: unknown) {
      console.error('Failed to save event:', error);
      
      // Extract specific error message from API response
      let errorMessage = 'Failed to save event. Please try again.';
      if (error instanceof Error) {
        errorMessage = error.message;
      }
      
      showErrorMessage(errorMessage);
    }
  };

  const handleDeleteEvent = async (eventId: number) => {
    try {
      await eventsApi.delete(eventId);
      
      // Refresh events from database
      const eventsData = await eventsApi.list();
      const mappedEvents: BrgEvent[] = eventsData.map(event => {
        // Use legendBrgGroups (all BRGs) to find colors, not brgGroups (only assigned BRGs)
        // This ensures events from unassigned BRGs still display correctly
        const brg = legendBrgGroups.find(b => b.id === event.brgId);
        const attendeesStr = event.acceptedAttendees > 0 ? `${event.acceptedAttendees} members` : '';
        
        return {
          id: event.eventId,
          eventName: event.eventName,
          brgId: event.brgId,
          brgName: event.brgName,
          brgColor: brgColorOrDefault(brg?.color),
          date: event.eventDate,
          time: event.eventTime,
          organizer: event.eventOrganizer,
          location: event.eventLocation || 'Microsoft Teams',
          acceptedAttendees: attendeesStr,
          eventDescription: event.eventDescription || '',
          createdBy: event.createdByEmail || '',
        };
      });

      setEvents(mappedEvents);
      setDeleteConfirmation(null);
      setSelectedEvent(null);
      showSuccessMessage('Event has been deleted from the BRG calendar');
    } catch (error) {
      console.error('Failed to delete event:', error);
      showErrorMessage('Failed to delete event. Please try again.');
    }
  };

  const handleEditEvent = (event: BrgEvent) => {
    setEditingEvent(event);
    setShowNewEventModal(true);
  };

  const monthYear = currentDate.toLocaleString('default', { month: 'long', year: 'numeric' });

  return (
    <>
      {/* Toast Notifications */}
      <div style={{
        position: 'fixed',
        top: '16px',
        right: '16px',
        zIndex: 999999,
        pointerEvents: 'none'
      }}>
        {toasts.map(toast => (
          <div
            key={toast.id}
            style={{
              marginBottom: '12px',
              pointerEvents: 'auto',
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              padding: '12px 16px',
              borderRadius: '6px',
              fontSize: '14px',
              fontWeight: '500',
              fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
              backgroundColor: toast.type === 'success' ? '#10b981' : '#ef4444',
              color: 'white',
              animation: 'slideIn 0.3s ease-out',
            }}
          >
            {toast.type === 'success' ? (
              <CheckCircle size={20} style={{ flexShrink: 0 }} />
            ) : (
              <AlertCircle size={20} style={{ flexShrink: 0 }} />
            )}
            {toast.message}
          </div>
        ))}
      </div>

      <style>{`
        @keyframes slideIn {
          from {
            transform: translateX(400px);
            opacity: 0;
          }
          to {
            transform: translateX(0);
            opacity: 1;
          }
        }
      `}</style>

      <div className="flex flex-col w-full h-full bg-white">

        {/* Main Content */}
        <div className="flex-1 overflow-auto">
        <div style={{ width: '100%', padding: '24px 80px', maxWidth: 'none' }}>
        {/* Filters Section */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '16px',
          marginBottom: '24px',
          flexWrap: 'wrap'
        }}>
          <FilterSelect
            label="BRG:"
            value={selectedBrgFilter}
            onChange={(e) => setSelectedBrgFilter(e.target.value)}
          >
            <option value="all">All BRGs</option>
            {legendBrgGroups.map(brg => (
              <option key={brg.id} value={brg.id}>
                {brg.name}
              </option>
            ))}
          </FilterSelect>

          <input
            type="text"
            placeholder="Search events..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            style={{
              padding: '8px 12px',
              border: '1px solid #d1d5db',
              borderRadius: '6px',
              fontSize: '14px',
              outline: 'none'
            }}
          />

          <button
            onClick={() => setShowNewEventModal(true)}
            style={{
              marginLeft: 'auto',
              padding: '8px 16px',
              backgroundColor: '#2563eb',
              color: 'white',
              fontWeight: 'bold',
              borderRadius: '6px',
              fontSize: '14px',
              border: 'none',
              cursor: 'pointer',
              transition: 'background-color 0.2s'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = '#1d4ed8';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = '#2563eb';
            }}
          >
            + New Event
          </button>
        </div>

        {/* Calendar Navigation */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-start',
          gap: '16px',
          marginBottom: '16px'
        }}>
          <button
            onClick={handlePrevMonth}
            style={{
              padding: '8px 12px',
              border: '1px solid #d1d5db',
              borderRadius: '6px',
              backgroundColor: 'white',
              cursor: 'pointer',
              fontSize: '18px',
              fontWeight: 'bold',
              color: '#333333',
              transition: 'background-color 0.2s'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = '#f3f4f6';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'white';
            }}
            aria-label="Previous month"
          >
            ‹
          </button>
          <h2 style={{
            fontSize: '24px',
            fontWeight: 'bold',
            minWidth: '180px',
            textAlign: 'center',
            margin: '0',
            color: '#111827'
          }}>{monthYear}</h2>
          <button
            onClick={handleNextMonth}
            style={{
              padding: '8px 12px',
              border: '1px solid #d1d5db',
              borderRadius: '6px',
              backgroundColor: 'white',
              cursor: 'pointer',
              fontSize: '18px',
              fontWeight: 'bold',
              color: '#333333',
              transition: 'background-color 0.2s'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = '#f3f4f6';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'white';
            }}
            aria-label="Next month"
          >
            ›
          </button>
        </div>

        {/* Calendar Grid */}
        <div className="mb-12" style={{ marginBottom: '16px' }}>
          <CalendarGrid
            days={filteredAndSearchedEvents}
            onEventClick={handleEventClick}
            onDayClick={handleDayClick}
          />
        </div>

        {/* BRG Color Key */}
        <div style={{
          padding: '16px',
          backgroundColor: '#f3f4f6',
          borderRadius: '6px',
          border: '1px solid #e5e7eb'
        }}>
          <h3 style={{ fontSize: '12px', fontWeight: 'bold', color: '#111827', marginBottom: '12px' }}>BRG Color Key</h3>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: '12px'
          }}>
            {legendBrgGroups.map(brg => (
              <div key={brg.id} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <div
                  style={{
                    width: '12px',
                    height: '12px',
                    borderRadius: '2px',
                    backgroundColor: brg.color,
                    flexShrink: 0
                  }}
                />
                <span style={{ fontSize: '12px', color: '#374151' }}>{brg.name}</span>
              </div>
            ))}
          </div>
        </div>
        </div>
      </div>
      </div>

      {/* Modals - Rendered outside main container for proper z-index stacking */}
      {selectedEvent && (
        <EventDetailModal
          event={selectedEvent}
          onClose={() => setSelectedEvent(null)}
          onEdit={(event) => {
            handleEditEvent(event);
            setSelectedEvent(null);
          }}
          onDelete={(eventId) => {
            setDeleteConfirmation({
              eventId: eventId,
              eventName: selectedEvent.eventName
            });
          }}
          currentUserEmail={currentUserEmail}
          isAdmin={isAdmin}
        />
      )}

      {deleteConfirmation && (
        <div style={{
          position: 'fixed',
          inset: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000000
        }}>
          {/* Overlay */}
          <div
            style={{
              position: 'absolute',
              inset: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
            }}
            onClick={() => setDeleteConfirmation(null)}
          />
          
          {/* Dialog */}
          <div style={{
            position: 'relative',
            backgroundColor: 'white',
            borderRadius: '8px',
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)',
            padding: '24px',
            maxWidth: '400px',
            zIndex: 1000001
          }}>
            <h2 style={{
              fontSize: '18px',
              fontWeight: 'bold',
              margin: '0 0 12px 0',
              color: '#111827'
            }}>
              Delete Event?
            </h2>
            <p style={{
              fontSize: '14px',
              color: '#6b7280',
              margin: '0 0 24px 0',
              lineHeight: '1.5'
            }}>
              Are you sure you want to delete "{deleteConfirmation.eventName}"? This action cannot be undone.
            </p>
            <div style={{
              display: 'flex',
              gap: '12px',
              justifyContent: 'flex-end'
            }}>
              <button
                onClick={() => setDeleteConfirmation(null)}
                style={{
                  padding: '8px 16px',
                  backgroundColor: '#f3f4f6',
                  color: '#374151',
                  border: 'none',
                  borderRadius: '4px',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'background-color 0.2s'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = '#e5e7eb';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = '#f3f4f6';
                }}
              >
                Cancel
              </button>
              <button
                onClick={() => handleDeleteEvent(deleteConfirmation.eventId)}
                style={{
                  padding: '8px 16px',
                  backgroundColor: '#ef4444',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'background-color 0.2s'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = '#dc2626';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = '#ef4444';
                }}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {selectedDay && (
        <DayViewModal
          day={selectedDay}
          onClose={() => setSelectedDay(null)}
          onEventClick={(event) => {
            setSelectedDay(null);
            setSelectedEvent(event);
          }}
        />
      )}

      {showNewEventModal && (
        <NewEventModal
          onClose={() => {
            setShowNewEventModal(false);
            setEditingEvent(null);
          }}
          onSave={handleSaveEvent}
          brgGroups={formBrgGroups}
          initialData={editingEvent || undefined}
          isEditMode={!!editingEvent}
          currentUserEmail={currentUserEmail}
          currentUserName={currentUserName}
          isAdmin={isAdmin}
        />
      )}
    </>
  );
}

